import { useEffect, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AppDialog from "components/common/AppDialog";
import CustomButton from "components/common/CustomButton";
import {
  IntegrationSettingsUpdateParams,
  VendorConnectionRead,
  useGetIntegrationSettingsQuery,
  useUpdateIntegrationSettingsMutation,
  useLazyGetIntegrationSettingsRevealQuery,
} from "features/integrations/integrations";

const CONNECTION_FIELD_ORDER: (keyof VendorConnectionRead)[] = [
  "host",
  "port",
  "username",
  "client_code",
  "password",
  "passphrase",
  "key",
  "access_token",
];

const FIELD_LABELS: Record<string, string> = {
  host: "Host",
  port: "Port",
  username: "Username",
  client_code: "Client Code",
  password: "Password",
  passphrase: "Passphrase",
  key: "Private Key",
  access_token: "Access Token",
};

const SECRET_FIELDS = new Set(["password", "passphrase", "key", "access_token"]);

const MASKED_PLACEHOLDER = "••••••••";

const MAX_DISPLAY_LENGTH = 64;

function truncate(value: string, maxLen: number = MAX_DISPLAY_LENGTH): string {
  if (typeof value !== "string" || value.length <= maxLen) return value;
  return value.slice(0, maxLen) + "…";
}

function ConnectionSection({
  title,
  connection,
  connectionLabel,
  onEdit,
  canEdit,
  revealedValues,
  onToggleSecret,
}: {
  title: string;
  connection: VendorConnectionRead;
  connectionLabel: string;
  onEdit: () => void;
  canEdit: boolean;
  revealedValues: Record<string, string>;
  onToggleSecret: (key: string) => void;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        height: { xs: "auto", md: "100%" },
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        p: 2,
        borderRadius: 2,
        border: "1px solid rgba(0,0,0,0.08)",
        boxShadow: 2,
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 0.75 }}>
        {title}
      </Typography>
      <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.75 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {connectionLabel}
        </Typography>
        <Chip
          label={connection.type.toUpperCase()}
          size="small"
          variant="outlined"
          color="default"
          sx={{
            height: 18,
            fontSize: 9,
            fontWeight: 700,
            flexShrink: 0,
            borderColor: "rgba(0,0,0,0.2)",
            color: "text.secondary",
            bgcolor: "transparent",
          }}
        />
        {canEdit && (
          <IconButton
            size="small"
            onClick={onEdit}
            aria-label={`Edit ${title}`}
            sx={{ p: 0.25, color: "text.secondary" }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 0.75,
          bgcolor: "grey.50",
          flex: { xs: "none", md: 1 },
          minHeight: { md: 0 },
          overflowY: { xs: "visible", md: "auto" },
        }}
      >
        {CONNECTION_FIELD_ORDER.filter((k) => k !== "id" && k !== "vendor" && k !== "type").map(
          (key) => {
            const label = FIELD_LABELS[key] ?? key;
            const value =
              connection[key as keyof VendorConnectionRead];
            const isSecret = SECRET_FIELDS.has(key);
            const isSet =
              isSecret &&
              (connection as unknown as Record<string, unknown>)[`${key}_is_set`];
            const displayValue =
              typeof value === "string" || value == null
                ? value ?? "—"
                : String(value);
            const revealed = revealedValues[key];
            const isRevealed = isSecret && revealed !== undefined;
            const showValue = isSecret && isSet
              ? (isRevealed ? revealed : "••••••")
              : displayValue;
            const toShow = typeof showValue === "string" && showValue.length > 0
              ? truncate(showValue)
              : "—";
            return (
              <Box
                key={key}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                py={0.25}
                gap={0.75}
              >
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {label}:
                </Typography>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.25}
                  minWidth={0}
                  sx={{ flex: 1, justifyContent: "flex-end" }}
                >
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      fontFamily: "monospace",
                      textAlign: "right",
                      maxWidth: { xs: 180, sm: 260, md: 320 },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {toShow}
                  </Typography>
                  {isSecret && isSet ? (
                    <Tooltip title={isRevealed ? "Hide value" : "Show value"}>
                      <IconButton
                        size="small"
                        onClick={() => onToggleSecret(key)}
                        aria-label={isRevealed ? "Hide" : "Show"}
                        sx={{ p: 0.25, color: "text.secondary" }}
                      >
                        {isRevealed ? (
                          <VisibilityOffIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <VisibilityIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Box>
              </Box>
            );
          }
        )}
      </Box>
    </Paper>
  );
}

function EditConnectionDialog({
  open,
  onClose,
  title,
  connection,
  draftFields,
  onSave,
  saving,
  onRevealSecret,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  connection: VendorConnectionRead;
  draftFields: Record<string, string | null> | null;
  onSave: (values: Record<string, string | null>) => void;
  saving: boolean;
  onRevealSecret?: (field: string) => Promise<string | null>;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [revealedInModal, setRevealedInModal] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (open && connection) {
      const initial: Record<string, string> = {};
      initial.vendor = draftFields?.vendor != null ? String(draftFields.vendor) : (connection.vendor ?? "");
      CONNECTION_FIELD_ORDER.forEach((k) => {
        if (k === "id" || k === "vendor" || k === "type") return;
        if (SECRET_FIELDS.has(k)) {
          const isSet = (connection as unknown as Record<string, unknown>)[`${k}_is_set`];
          initial[k] = isSet ? MASKED_PLACEHOLDER : "";
          return;
        }
        const fromDraft = draftFields && k in draftFields ? draftFields[k] : undefined;
        const fromConnection = connection[k as keyof VendorConnectionRead];
        const v = fromDraft !== undefined && fromDraft !== null
          ? String(fromDraft)
          : fromConnection != null && typeof fromConnection === "string"
            ? fromConnection
            : "";
        initial[k] = v;
      });
      setValues(initial);
      setRevealedInModal({});
    }
  }, [open, connection, draftFields]);

  const handleChange = (key: string, value: string) => {
    setValues((prev) => {
      const prevVal = prev[key];
      if (SECRET_FIELDS.has(key) && prevVal === MASKED_PLACEHOLDER) {
        return { ...prev, [key]: value.replace(/^•+/, "") };
      }
      return { ...prev, [key]: value };
    });
  };

  const handleReveal = async (key: string) => {
    if (!onRevealSecret) return;
    try {
      const value = await onRevealSecret(key);
      if (value != null) {
        setValues((prev) => ({ ...prev, [key]: value }));
        setRevealedInModal((prev) => ({ ...prev, [key]: true }));
      }
    } catch {
    }
  };

  const handleSubmit = () => {
    const out: Record<string, string | null> = {};
    if (values.vendor !== undefined) {
      out.vendor = values.vendor.trim() || null;
    }
    CONNECTION_FIELD_ORDER.forEach((k) => {
      if (k === "id" || k === "vendor" || k === "type") return;
      if (k in values) {
        const v = values[k];
        if (v === "" || v === MASKED_PLACEHOLDER) return;
        out[k] = v;
      }
    });
    onSave(Object.keys(out).length ? out : {});
    setValues({});
    setRevealedInModal({});
    onClose();
  };

  const handleClose = () => {
    setValues({});
    setRevealedInModal({});
    onClose();
  };

  const fieldsToShow: (keyof VendorConnectionRead)[] = ["vendor", ...CONNECTION_FIELD_ORDER.filter((k) => k !== "id" && k !== "vendor" && k !== "type")];

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title={`Edit ${title}`}
      maxWidth="sm"
        actions={
          <>
            <CustomButton fullWidth={false} variant="outlined" onClick={handleClose} disabled={saving}>
              Cancel
            </CustomButton>
            <CustomButton fullWidth={false} onClick={handleSubmit} disabled={saving}>
              Save
            </CustomButton>
          </>
        }
    >
      {fieldsToShow.map((key) => {
          const label = key === "vendor" ? "Connection name" : (FIELD_LABELS[key] ?? key);
          const isSecret = SECRET_FIELDS.has(key);
          const isSet = isSecret && (connection as unknown as Record<string, unknown>)[`${key}_is_set`];
          return (
            <Box key={key} sx={{ position: "relative" }}>
              <TextField
                fullWidth
                margin="dense"
                label={label}
                type={isSecret && !revealedInModal[key] ? "password" : "text"}
                placeholder={
                  isSecret && isSet && !(values[key]?.length)
                    ? "••••••••"
                    : undefined
                }
                helperText={
                  isSecret && isSet && !(values[key]?.length)
                    ? "Leave blank to keep current"
                    : undefined
                }
                value={values[key] ?? ""}
                onChange={(e) => handleChange(key, e.target.value)}
                disabled={saving}
                InputProps={
                  isSecret && isSet && onRevealSecret
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title={revealedInModal[key] ? "Hide" : "Show current value"}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (revealedInModal[key]) {
                                    setRevealedInModal((p) => ({ ...p, [key]: false }));
                                    setValues((p) => ({ ...p, [key]: MASKED_PLACEHOLDER }));
                                  } else {
                                    handleReveal(key);
                                  }
                                }}
                                edge="end"
                              >
                                {revealedInModal[key] ? (
                                  <VisibilityOffIcon fontSize="small" />
                                ) : (
                                  <VisibilityIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
            </Box>
          );
        })}
    </AppDialog>
  );
}

type DraftState = {
  enabled: boolean;
  source: Record<string, string | null> | null;
  destination: Record<string, string | null> | null;
};

export default function IntegrationSettingsPanel({
  connectionMatchId,
  canEdit,
  onSuccess,
}: {
  connectionMatchId: number;
  canEdit: boolean;
  onSuccess?: (message: string) => void;
}) {
  const { data: settings, isLoading } = useGetIntegrationSettingsQuery(
    connectionMatchId,
    { skip: !connectionMatchId }
  );
  const [updateSettings, { isLoading: saving }] =
    useUpdateIntegrationSettingsMutation();
  const [fetchReveal] = useLazyGetIntegrationSettingsRevealQuery();

  const [draft, setDraft] = useState<DraftState>({
    enabled: false,
    source: null,
    destination: null,
  });
  const [revealedValues, setRevealedValues] = useState<{
    source: Record<string, string>;
    destination: Record<string, string>;
  }>({ source: {}, destination: {} });
  const [editSourceOpen, setEditSourceOpen] = useState(false);
  const [editDestOpen, setEditDestOpen] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; isError?: boolean }>({
    open: false,
    message: "",
  });

  useEffect(() => {
    if (settings) {
      setDraft({
        enabled: settings.enabled,
        source: null,
        destination: null,
      });
    }
  }, [settings]);

  const hasUnsaved =
    settings != null &&
    (draft.enabled !== settings.enabled ||
      (draft.source != null && Object.keys(draft.source).length > 0) ||
      (draft.destination != null && Object.keys(draft.destination).length > 0));

  const handleEnabledChange = (checked: boolean) => {
    if (!canEdit) return;
    setDraft((prev) => ({ ...prev, enabled: checked }));
  };

  const handleSaveSourceDraft = (values: Record<string, string | null>) => {
    setDraft((prev) => ({
      ...prev,
      source: Object.keys(values).length ? values : null,
    }));
    setEditSourceOpen(false);
  };

  const handleSaveDestinationDraft = (values: Record<string, string | null>) => {
    setDraft((prev) => ({
      ...prev,
      destination: Object.keys(values).length ? values : null,
    }));
    setEditDestOpen(false);
  };

  const handleToggleSecret = async (
    role: "source" | "destination",
    field: string
  ) => {
    const current = revealedValues[role][field];
    if (current !== undefined) {
      const next = { ...revealedValues[role] };
      delete next[field];
      setRevealedValues((prev) => ({ ...prev, [role]: next }));
      return;
    }
    try {
      const result = await fetchReveal({
        connectionMatchId,
        connection: role,
        field,
      });
      const value = result?.data?.value ?? "";
      setRevealedValues((prev) => ({
        ...prev,
        [role]: { ...prev[role], [field]: value },
      }));
    } catch {
      setSnackbar({
        open: true,
        message: "Could not load value (Super Admin only)",
        isError: true,
      });
    }
  };

  const handleConfirmSave = async () => {
    if (!canEdit || !settings) return;
    try {
      await updateSettings({
        connectionMatchId,
        body: {
          enabled: draft.enabled,
          ...(draft.source && { source: draft.source as IntegrationSettingsUpdateParams["source"] }),
          ...(draft.destination && {
            destination: draft.destination as IntegrationSettingsUpdateParams["destination"],
          }),
        },
      }).unwrap();
      setDraft({
        enabled: draft.enabled,
        source: null,
        destination: null,
      });
      setConfirmSaveOpen(false);
      setSnackbar({ open: true, message: "Settings saved successfully" });
      onSuccess?.("Settings saved successfully");
    } catch {
      setSnackbar({ open: true, message: "Failed to save settings", isError: true });
      onSuccess?.("Failed to save settings");
    }
  };

  if (isLoading || !settings) {
    return (
      <Box sx={{ py: 2 }}>
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 1 }} />
      </Box>
    );
  }

  const handleRevealForModal = (role: "source" | "destination") => async (field: string): Promise<string | null> => {
    try {
      const result = await fetchReveal({ connectionMatchId, connection: role, field });
      return result?.data?.value ?? null;
    } catch {
      return null;
    }
  };

  return (
    <Box sx={{ py: 1.5 }}>
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        gap={1.5}
        sx={{ mb: 1.5 }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" fontWeight={600}>
            Enabled
          </Typography>
          <Switch
            checked={draft.enabled}
            onChange={(_, checked) => handleEnabledChange(checked)}
            disabled={!canEdit}
            color="primary"
            size="small"
          />
          {canEdit && (
            <CustomButton
              fullWidth={false}
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setConfirmSaveOpen(true)}
              disabled={!hasUnsaved || saving}
            >
              Save
            </CustomButton>
          )}
        </Box>
        {!canEdit && (
          <Typography variant="caption" color="text.secondary">
            Only Integration Dashboard users or Super Admin can change
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
          gap: 1.5,
          alignItems: "stretch",
        }}
      >
        <ConnectionSection
          title="SOURCE"
          connection={settings.source}
          connectionLabel={(draft.source?.vendor != null ? draft.source.vendor : null) ?? settings.source.vendor}
          onEdit={() => setEditSourceOpen(true)}
          canEdit={canEdit}
          revealedValues={revealedValues.source}
          onToggleSecret={(field) => handleToggleSecret("source", field)}
        />
        <ConnectionSection
          title="DESTINATION"
          connection={settings.destination}
          connectionLabel={(draft.destination?.vendor != null ? draft.destination.vendor : null) ?? settings.destination.vendor}
          onEdit={() => setEditDestOpen(true)}
          canEdit={canEdit}
          revealedValues={revealedValues.destination}
          onToggleSecret={(field) => handleToggleSecret("destination", field)}
        />
      </Box>

      <AppDialog
        open={confirmSaveOpen}
        onClose={() => !saving && setConfirmSaveOpen(false)}
        title="Save changes?"
        actions={
          <>
            <CustomButton
              fullWidth={false}
              variant="outlined"
              onClick={() => setConfirmSaveOpen(false)}
              disabled={saving}
            >
              Cancel
            </CustomButton>
            <CustomButton fullWidth={false} onClick={handleConfirmSave} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </CustomButton>
          </>
        }
      >
        <Typography>
          Save all changes to integration settings (enabled status and connection fields)?
        </Typography>
      </AppDialog>

      <EditConnectionDialog
        open={editSourceOpen}
        onClose={() => setEditSourceOpen(false)}
        title="Source connection"
        connection={settings.source}
        draftFields={draft.source}
        onSave={handleSaveSourceDraft}
        saving={false}
        onRevealSecret={handleRevealForModal("source")}
      />
      <EditConnectionDialog
        open={editDestOpen}
        onClose={() => setEditDestOpen(false)}
        title="Destination connection"
        connection={settings.destination}
        draftFields={draft.destination}
        onSave={handleSaveDestinationDraft}
        saving={false}
        onRevealSecret={handleRevealForModal("destination")}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
        ContentProps={{
          sx: { bgcolor: snackbar.isError ? "error.main" : "success.main", color: "white" },
        }}
      />
    </Box>
  );
}
